import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Search, Tag, SortByAlpha, TrendingUp } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

// Fast hashtag extraction utility - only processes when needed
const extractHashtagsFromQuery = (query) => {
  const hashtags = [];

  // Extract from topics field (faster string parsing)
  if (query.topics && typeof query.topics === 'string') {
    try {
      // Fast regex approach instead of JSON.parse
      const hashtagMatches = query.topics.match(/#\w+/g);
      if (hashtagMatches) {
        hashtags.push(...hashtagMatches);
      }
    } catch (e) {
      // Fallback to JSON parsing if regex fails
      try {
        const topics = JSON.parse(query.topics);
        if (Array.isArray(topics)) {
          hashtags.push(...topics.filter((t) => typeof t === 'string' && t.startsWith('#')));
        }
      } catch (fallbackError) {
        // Ignore malformed data
      }
    }
  }

  // Extract from qwotedTags field (optimized)
  if (query.qwotedTags?.length) {
    query.qwotedTags.forEach((tag) => {
      if (tag?.hashtag?.startsWith?.('#')) {
        hashtags.push(tag.hashtag);
      }
    });
  }

  return [...new Set(hashtags)]; // Remove duplicates
};

// Debounced search hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const HashtagFilter = ({ open, onClose, queries, selectedHashtags, onHashtagsChange }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hashtagData, setHashtagData] = useState(null);
  const [sortBy, setSortBy] = useState('count'); // 'alpha' or 'count'

  // Debounce search to prevent excessive filtering
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Extract hashtags only when drawer opens (lazy loading)
  useEffect(() => {
    if (!open || !queries?.length || hashtagData) return;

    setIsProcessing(true);

    // Use setTimeout to not block the UI
    const timeoutId = setTimeout(() => {
      try {
        const hashtagMap = new Map();
        const allHashtags = new Set();

        // Process queries in batches to prevent blocking
        const batchSize = 50;
        let processed = 0;

        const processBatch = () => {
          const batch = queries.slice(processed, processed + batchSize);

          batch.forEach((query) => {
            if (query?.id) {
              const hashtags = extractHashtagsFromQuery(query);
              if (hashtags.length > 0) {
                hashtagMap.set(query.id, hashtags);
                hashtags.forEach((tag) => allHashtags.add(tag));
              }
            }
          });

          processed += batchSize;

          if (processed < queries.length) {
            // Continue processing in next tick
            setTimeout(processBatch, 0);
          } else {
            // All done - create counts
            const counts = {};
            const hashtagArray = Array.from(allHashtags);

            hashtagArray.forEach((hashtag) => {
              counts[hashtag] = 0;
              hashtagMap.forEach((queryHashtags) => {
                if (queryHashtags.includes(hashtag)) {
                  counts[hashtag]++;
                }
              });
            });

            setHashtagData({
              allHashtags: hashtagArray,
              hashtagMap,
              counts,
            });
            setIsProcessing(false);
          }
        };

        processBatch();
      } catch (error) {
        console.error('Error processing hashtags:', error);
        setIsProcessing(false);
      }
    }, 100); // Small delay to let drawer animation start

    return () => clearTimeout(timeoutId);
  }, [open, queries, hashtagData]);

  // Sort hashtags based on current sort option
  const sortedHashtags = useMemo(() => {
    if (!hashtagData?.allHashtags) return [];

    const hashtags = [...hashtagData.allHashtags];

    if (sortBy === 'count') {
      // Sort by count (descending), then alphabetically for ties
      return hashtags.sort((a, b) => {
        const countDiff = hashtagData.counts[b] - hashtagData.counts[a];
        if (countDiff !== 0) return countDiff;
        return a.toLowerCase().localeCompare(b.toLowerCase());
      });
    } else {
      // Sort alphabetically
      return hashtags.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }
  }, [hashtagData, sortBy]);

  // Filter hashtags based on search with memoization
  const filteredHashtags = useMemo(() => {
    if (!sortedHashtags.length) return [];
    if (!debouncedSearch.trim()) return sortedHashtags;

    const searchTerm = debouncedSearch.toLowerCase();
    return sortedHashtags.filter((hashtag) => hashtag.toLowerCase().includes(searchTerm));
  }, [sortedHashtags, debouncedSearch]);

  // Limit displayed hashtags for performance (virtual scrolling alternative)
  const [displayLimit, setDisplayLimit] = useState(100);
  const displayedHashtags = useMemo(() => filteredHashtags.slice(0, displayLimit), [filteredHashtags, displayLimit]);

  const handleHashtagToggle = useCallback(
    (hashtag) => {
      const newSelected = new Set(selectedHashtags);
      if (newSelected.has(hashtag)) {
        newSelected.delete(hashtag);
      } else {
        newSelected.add(hashtag);
      }
      onHashtagsChange(newSelected);
    },
    [selectedHashtags, onHashtagsChange]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedHashtags.size === filteredHashtags.length) {
      const newSelected = new Set(selectedHashtags);
      filteredHashtags.forEach((hashtag) => newSelected.delete(hashtag));
      onHashtagsChange(newSelected);
    } else {
      const newSelected = new Set([...selectedHashtags, ...filteredHashtags]);
      onHashtagsChange(newSelected);
    }
  }, [selectedHashtags, filteredHashtags, onHashtagsChange]);

  const handleClearAll = useCallback(() => {
    onHashtagsChange(new Set());
  }, [onHashtagsChange]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    onClose();
  }, [onClose]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSortChange = useCallback((event, newSort) => {
    if (newSort !== null) {
      setSortBy(newSort);
    }
  }, []);

  const loadMoreHashtags = useCallback(() => {
    setDisplayLimit((prev) => prev + 100);
  }, []);

  const selectedFilteredCount = useMemo(
    () => filteredHashtags.filter((hashtag) => selectedHashtags.has(hashtag)).length,
    [filteredHashtags, selectedHashtags]
  );

  // Reset processed data when drawer closes
  useEffect(() => {
    if (!open) {
      setHashtagData(null);
      setDisplayLimit(100);
      setSortBy('count');
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
          background: theme.palette.background.paper,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tag />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Hashtag Filter
              </Typography>
            </Box>
          </Box>

          {selectedHashtags.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedHashtags.size} of {hashtagData?.allHashtags?.length} selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAll}
                sx={{
                  marginLeft: 'auto',
                  mb: 1,
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Clear All
              </Button>
            </Box>
          )}
          {/* Sort Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.75rem' }}>Sort by:</Typography>
            <ToggleButtonGroup
              value={sortBy}
              exclusive
              onChange={handleSortChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  color: 'white',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: 'white',
                      color: theme.palette.primary.main,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="count" aria-label="sort by popularity" sx={{ minWidth: 120 }}>
                <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                Popular
              </ToggleButton>
              <ToggleButton value="alpha" aria-label="sort alphabetically" sx={{ minWidth: 120 }}>
                <SortByAlpha sx={{ fontSize: 16, mr: 0.5 }} />
                A-Z
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {isProcessing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading hashtags...
            </Typography>
          </Box>
        ) : !hashtagData ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No hashtags available
            </Typography>
          </Box>
        ) : (
          <>
            {/* Search */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                placeholder="Search hashtags..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                }}
              />
            </Box>

            {/* Selected Hashtags Preview */}
            {selectedHashtags.size > 0 && (
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Selected Hashtags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.from(selectedHashtags)
                    .slice(0, 10)
                    .map((hashtag) => (
                      <Chip
                        key={hashtag}
                        label={hashtag}
                        size="small"
                        onDelete={() => handleHashtagToggle(hashtag)}
                        color="primary"
                        variant="filled"
                      />
                    ))}
                  {selectedHashtags.size > 10 && <Chip label={`+${selectedHashtags.size - 10} more`} size="small" variant="outlined" />}
                </Box>
              </Box>
            )}

            {/* Hashtag List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {displayedHashtags.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No hashtags found matching your search' : 'No hashtags available'}
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ p: 1 }}>
                  {displayedHashtags.map((hashtag) => (
                    <ListItem
                      key={hashtag}
                      sx={{
                        p: 0.5,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                        ...(selectedHashtags.has(hashtag) && {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        }),
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={selectedHashtags.has(hashtag)} onChange={() => handleHashtagToggle(hashtag)} size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {hashtag}
                            </Typography>
                            <Box
                              sx={{
                                ml: 1,
                                minWidth: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: sortBy === 'count' ? theme.palette.secondary.main : theme.palette.primary.main,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            >
                              {hashtagData.counts[hashtag] || 0}
                            </Box>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </ListItem>
                  ))}

                  {/* Load More Button */}
                  {displayedHashtags.length < filteredHashtags.length && (
                    <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                      <Button variant="outlined" size="small" onClick={loadMoreHashtags}>
                        Load More ({filteredHashtags.length - displayedHashtags.length} remaining)
                      </Button>
                    </ListItem>
                  )}
                </List>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

HashtagFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  queries: PropTypes.array.isRequired,
  selectedHashtags: PropTypes.instanceOf(Set).isRequired,
  onHashtagsChange: PropTypes.func.isRequired,
};

export default HashtagFilter;
